import { Autocomplete, FormControl, FormHelperText, TextField } from "@mui/material";
import { useController, UseControllerProps, FieldValues } from "react-hook-form";
import AutocompleteInputOption from "../models/autocompleteInputOption";
import { useEffect, useState, useMemo } from "react";

interface Props<T extends FieldValues> extends UseControllerProps<T> {
    label: string;
    items: AutocompleteInputOption[];
    selectedids?: string;
    multiple?: boolean;
    placeholder?: string;
    onChange?: (event: React.SyntheticEvent<Element, Event>, value: AutocompleteInputOption | AutocompleteInputOption[] | null) => void;
}

export default function AppAutocompleteWithDraft<T extends FieldValues>({ label, items = [], selectedids, multiple, placeholder, onChange, ...props }: Props<T>) {
    const { fieldState, field } = useController<T>({ ...props, rules: { ...props.rules } });

    const [singleValue, setSingleValue] = useState<AutocompleteInputOption | null>(null);
    const [multiValues, setMultiValues] = useState<AutocompleteInputOption[]>([]);

    // Bezpiecznie sortuj opcje z obsługą pustej tablicy
    const sortedOptions = useMemo(() => {
        // Upewnij się, że items jest tablicą i nie jest pusta
        if (!items || !Array.isArray(items) || items.length === 0) return [];
        
        // Filtruj elementy null/undefined dla bezpieczeństwa
        const validItems = items.filter(item => item !== null && item !== undefined);
        
        try {
            return [...validItems].sort((a, b) => {
                // Zabezpieczenie przed null/undefined
                const nameA = a?.name || '';
                const nameB = b?.name || '';
                return -nameB.localeCompare(nameA);
            });
        } catch (error) {
            console.error("Błąd podczas sortowania:", error);
            return validItems; // Zwróć nieposortowane, ale przefiltrowane elementy
        }
    }, [items]);

    // Aktualizacja gdy zmienia się selectedids
    useEffect(() => {
        // Upewnij się, że items istnieje i ma elementy
        if (!items || !Array.isArray(items) || items.length === 0) {
            multiple ? setMultiValues([]) : setSingleValue(null);
            return;
        }

        if (selectedids) {
            try {
                const selectedIdsList = selectedids.split(',').map(id => id.trim());
                const defaultValues = items.filter(item => 
                    item && item.id && selectedIdsList.includes(item.id.toString())
                );
                
                if (multiple) {
                    setMultiValues(defaultValues || []);
                } else {
                    setSingleValue(defaultValues[0] || null);
                }
            } catch (error) {
                console.error("Błąd przetwarzania selectedids:", error);
                multiple ? setMultiValues([]) : setSingleValue(null);
            }
        } else {
            multiple ? setMultiValues([]) : setSingleValue(null);
        }
    }, [items, selectedids, multiple]);

    // Obsługa zmian field.value
    useEffect(() => {
        // Upewnij się, że items istnieje i ma elementy
        if (!items || !Array.isArray(items) || items.length === 0 || field.value === undefined) {
            return;
        }

        try {
            const valueStr = String(field.value || '');
            if (valueStr) {
                const matchingItem = items.find(item => 
                    item && item.id && item.id.toString() === valueStr
                );
                
                if (matchingItem) {
                    if (multiple) {
                        setMultiValues([matchingItem]);
                    } else {
                        setSingleValue(matchingItem);
                    }
                }
            }
        } catch (error) {
            console.error("Błąd przetwarzania field.value:", error);
        }
    }, [field.value, items, multiple]);

    return (
        <FormControl fullWidth error={!!fieldState.error}>
            <Autocomplete
                multiple={multiple}
                fullWidth
                value={multiple ? multiValues : singleValue}
                options={sortedOptions}
                getOptionLabel={(option) => {
                    if (!option) return '';
                    return `${option.name || ''} - id: ${option.id || ''}`;
                }}
                isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder={placeholder}
                        error={!!fieldState.error}
                    />
                )}
                onChange={(event, newValues) => {
                    try {
                        if (multiple) {
                            const multiVal = (newValues as AutocompleteInputOption[]) || [];
                            setMultiValues(multiVal);
                            const validValues = multiVal
                                .filter(v => v && v.id)
                                .map(v => v.id)
                                .join(',');
                            field.onChange(validValues);
                        } else {
                            const newValue = newValues as AutocompleteInputOption | null;
                            setSingleValue(newValue);
                            field.onChange(newValue?.id ?? '');
                        }
                        if (onChange) {
                            onChange(event, newValues);
                        }
                    } catch (error) {
                        console.error("Błąd w handleChange:", error);
                        field.onChange(multiple ? '' : '');
                    }
                }}
            />
            <FormHelperText>{fieldState.error?.message}</FormHelperText>
        </FormControl>
    );
}